// theme/tokens.js
//
// Single source of truth for the brand's visual language: colors, type
// scale, radii, shadows, and motion. Every component should import from
// here instead of redeclaring its own local `tokens`/`BRAND` object
// (ClientItem.js and TopClientConnect.js currently do that — point them
// at this file and delete their local copies to keep the whole site in
// sync).

export const tokens = {
  color: {
    // --- Brand ---
    teal: "#0F8B8D", // primary brand color — CTAs, links, active states
    tealDark: "#0A5F60", // hover/active/pressed states for teal
    tealLight: "rgba(15,138,141,0.12)", // tints — badge backgrounds, subtle fills

    // --- Ink (neutral / text) ---
    ink: "#12242E", // primary text, headings
    inkMuted: "rgba(18,36,46,0.65)", // secondary/body text
    inkFaint: "rgba(18,36,46,0.45)", // placeholders, disabled text
    line: "rgba(18,36,46,0.10)", // borders, dividers, hairlines

    // --- Surfaces ---
    surface: "#FFFFFF", // cards, modals, inputs
    surfaceAlt: "#F7F8FA", // page background, alternating sections
    surfaceMuted: "#F5F7F8", // subtle fills inside cards (e.g. logo tiles)
    overlay: "rgba(18,36,46,0.48)", // modal/drawer backdrops

    // --- Semantic (status) ---
    success: "#1E8E5A",
    successBg: "rgba(30,142,90,0.10)",
    warning: "#B98900",
    warningBg: "rgba(185,137,0,0.10)",
    error: "#C0392B",
    errorBg: "rgba(192,57,43,0.10)",
    info: "#0F6FB9",
    infoBg: "rgba(15,111,185,0.10)",

    // --- Fixed ---
    white: "#FFFFFF",
    black: "#000000",
  },

  font: {
    heading: "'Sora', sans-serif",
    // NOTE: assuming a body font — swap 'Inter' for whatever you actually
    // load alongside Sora (or just reuse heading everywhere if you only
    // ship one typeface).
    body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },

  // Full type scale. `fontFamily` on each entry maps directly to MUI's
  // `theme.typography.<variant>`, so this doubles as the createTheme input
  // in theme.js below.
  type: {
    display: {
      fontFamily: "heading",
      fontSize: "3rem",
      lineHeight: 1.15,
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h1: {
      fontFamily: "heading",
      fontSize: "2.5rem",
      lineHeight: 1.2,
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    h2: {
      fontFamily: "heading",
      fontSize: "2rem",
      lineHeight: 1.25,
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: "heading",
      fontSize: "1.5rem",
      lineHeight: 1.3,
      fontWeight: 700,
    },
    h4: {
      fontFamily: "heading",
      fontSize: "1.25rem",
      lineHeight: 1.35,
      fontWeight: 700,
    },
    h5: {
      fontFamily: "heading",
      fontSize: "1.125rem",
      lineHeight: 1.4,
      fontWeight: 600,
    },
    h6: {
      fontFamily: "heading",
      fontSize: "1rem",
      lineHeight: 1.4,
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: "body",
      fontSize: "1rem",
      lineHeight: 1.5,
      fontWeight: 600,
    },
    subtitle2: {
      fontFamily: "body",
      fontSize: "0.875rem",
      lineHeight: 1.5,
      fontWeight: 600,
    },
    body1: {
      fontFamily: "body",
      fontSize: "1rem",
      lineHeight: 1.65,
      fontWeight: 400,
    },
    body2: {
      fontFamily: "body",
      fontSize: "0.875rem",
      lineHeight: 1.6,
      fontWeight: 400,
    },
    caption: {
      fontFamily: "body",
      fontSize: "0.75rem",
      lineHeight: 1.5,
      fontWeight: 400,
    },
    overline: {
      fontFamily: "body",
      fontSize: "0.75rem",
      lineHeight: 1.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
    button: {
      fontFamily: "body",
      fontSize: "0.875rem",
      lineHeight: 1.5,
      fontWeight: 600,
      textTransform: "none",
    },
  },

  // Text-color roles, meant to be paired with a background role below
  // (e.g. `onLight.primary` on `color.surface`, `onDark.primary` on `color.ink`).
  textOn: {
    light: {
      primary: "#12242E", // == color.ink
      secondary: "rgba(18,36,46,0.65)",
      disabled: "rgba(18,36,46,0.38)",
      brand: "#0F8B8D",
    },
    dark: {
      primary: "#FFFFFF",
      secondary: "rgba(255,255,255,0.72)",
      disabled: "rgba(255,255,255,0.40)",
      brand: "#5FD1D3", // lightened teal — keeps AA contrast on a dark/ink bg
    },
  },

  // Named background roles, so "what bg do I use for a card vs. a page vs.
  // a dark banner" is a lookup, not a judgment call per component.
  bg: {
    page: "#F7F8FA",
    surface: "#FFFFFF",
    surfaceMuted: "#F5F7F8",
    brand: "#0F8B8D",
    brandDark: "#0A5F60",
    inkSection: "#12242E", // for dark/contrast sections (footers, CTA bands)
  },

  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    pill: 999,
  },

  shadow: {
    sm: "0 2px 10px rgba(18,36,46,0.05)",
    md: "0 10px 24px rgba(18,36,46,0.10)",
    lg: "0 20px 44px rgba(18,36,46,0.16)",
  },

  // Button sizing, keyed to the same three steps used by MUI's size prop.
  // fontSize intentionally steps around `type.button` (0.875rem) rather than
  // just repeating it at every size, so a "small" button doesn't carry the
  // same type weight as a "large" one.
  button: {
    height: { sm: 32, md: 40, lg: 48 },
    paddingX: { sm: 14, md: 20, lg: 28 },
    fontSize: { sm: "0.8125rem", md: "0.875rem", lg: "0.9375rem" }, // 13 / 14 / 15px
    radius: 8,
  },

  // Text fields, selects, search boxes — one spec so every input on the
  // site (regardless of MUI component) lines up to the same height/radius
  // and reacts to hover/focus/error the same way.
  input: {
    height: { sm: 36, md: 44, lg: 52 },
    fontSize: { sm: "0.8125rem", md: "0.875rem", lg: "0.9375rem" },
    radius: 10,
    bg: "#FFFFFF",
    bgMuted: "#F5F7F8", // for a filled/search-style input on a colored section
    placeholder: "rgba(18,36,46,0.45)",
    border: "rgba(18,36,46,0.10)", // resting
    borderHover: "rgba(18,36,46,0.24)",
    borderFocus: "#0F8B8D",
    borderError: "#C0392B",
    label: "rgba(18,36,46,0.65)",
  },

  // Hero/banner section presets. Pick a variant per section rather than
  // hand-rolling a background + text color pairing each time — see
  // presets.js for ready-to-spread sx objects built from these.
  banner: {
    primary: {
      background: "linear-gradient(135deg, #0F8B8D 0%, #0A5F60 100%)",
      text: "#FFFFFF",
      textMuted: "rgba(255,255,255,0.78)",
    },
    dark: {
      background: "#12242E",
      text: "#FFFFFF",
      textMuted: "rgba(255,255,255,0.68)",
    },
    light: {
      background: "#F7F8FA",
      text: "#12242E",
      textMuted: "rgba(18,36,46,0.65)",
    },
  },

  // Card surface presets — "elevated" for default content cards,
  // "outlined" for filter bars / low-emphasis containers, "flat" for
  // cards that sit inside another card (e.g. a list row) and shouldn't
  // add their own shadow.
  card: {
    padding: { sm: 16, md: 24 },
    radius: 12,
    elevated: {
      background: "#FFFFFF",
      border: "1px solid rgba(18,36,46,0.10)",
      shadowResting: "0 2px 10px rgba(18,36,46,0.05)",
      shadowHover: "0 10px 24px rgba(18,36,46,0.10)",
      borderHover: "#0F8B8D",
    },
    outlined: {
      background: "#FFFFFF",
      border: "1px dashed rgba(18,36,46,0.16)",
      shadowResting: "none",
      shadowHover: "none",
      borderHover: "rgba(18,36,46,0.24)",
    },
    flat: {
      background: "transparent",
      border: "none",
      shadowResting: "none",
      shadowHover: "none",
      borderHover: "transparent",
    },
  },

  space: (multiplier = 1) => `${multiplier * 8}px`,
};

// Shared keyframes, applied via sx={{ ...fadeInUp, animation: "fadeInUp .5s ease both" }}
export const fadeInUp = {
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export const fadeIn = {
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
};